import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * useHudLayout — persistent layout state for the HUD workspace.
 *
 * @param {string} storageKey  localStorage key.
 * @param {Array}  panels      [{ id, defaultRect:{x,y,w,h}, defaultOpen? }]
 * @returns {[object, function, function, object, function]}
 *   [layout, setPanel(id, patch), resetLayout(), visible, toggleVisible(id, next?)]
 *
 *   layout[id]  = { x, y, w, h, open, collapsed }
 *   visible[id] = boolean (mirror of layout[id].open, convenience for menus)
 */
export default function useHudLayout(storageKey, panels) {
  // Stable signature of the incoming defaults so effects don't loop.
  const defaults = useMemo(() => {
    const out = {};
    for (const p of panels) {
      const r = p.defaultRect || {};
      out[p.id] = {
        x: Number.isFinite(r.x) ? r.x : 24,
        y: Number.isFinite(r.y) ? r.y : 24,
        w: Number.isFinite(r.w) ? r.w : 320,
        h: Number.isFinite(r.h) ? r.h : 240,
        open: p.defaultOpen !== false,
        collapsed: false,
      };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels.map((p) => p.id).join("|"), storageKey]);

  const load = useCallback(() => {
    let saved = {};
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) saved = JSON.parse(raw) || {};
    } catch {
      saved = {};
    }
    // Merge: defaults first, then any persisted per-panel patch (only for
    // panels that still exist).
    const merged = {};
    for (const id of Object.keys(defaults)) {
      merged[id] = { ...defaults[id], ...(saved[id] || {}) };
    }
    return merged;
  }, [defaults, storageKey]);

  const [layout, setLayout] = useState(load);

  // Reload when the set of panels / storageKey changes.
  useEffect(() => {
    setLayout(load());
  }, [load]);

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch {
      /* storage full or unavailable — ignore */
    }
  }, [layout, storageKey]);

  const setPanel = useCallback((id, patch) => {
    setLayout((prev) => {
      const cur = prev[id];
      if (!cur) return prev;
      const next = typeof patch === "function" ? patch(cur) : patch;
      return { ...prev, [id]: { ...cur, ...next } };
    });
  }, []);

  const resetLayout = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    // Fresh clone so nested objects aren't shared with the memoized defaults.
    const fresh = {};
    for (const id of Object.keys(defaults)) fresh[id] = { ...defaults[id] };
    setLayout(fresh);
  }, [defaults, storageKey]);

  const toggleVisible = useCallback((id, next) => {
    setLayout((prev) => {
      const cur = prev[id];
      if (!cur) return prev;
      const open = typeof next === "boolean" ? next : !cur.open;
      return { ...prev, [id]: { ...cur, open } };
    });
  }, []);

  const visible = useMemo(() => {
    const v = {};
    for (const id of Object.keys(layout)) v[id] = !!layout[id].open;
    return v;
  }, [layout]);

  return [layout, setPanel, resetLayout, visible, toggleVisible];
}
